<template>
  <div id="insider-backtester-transactions-filter-tab">
    <v-container fluid pa-0>
      <v-row>
        <!-- Transaction Types -->
        <v-col cols="12" mb-4>
          <helpers-section-sub-header class="mb-2"> Transaction types: </helpers-section-sub-header>
          <v-radio-group v-model="clusterCriteria.transaction_type" row hide-details>
            <v-radio :value="2" label="All Buy transactions" color="primary" />
            <v-radio :value="4" label="Open Market purchases" color="primary" />
          </v-radio-group>
        </v-col>

        <!-- Date Range -->
        <v-col cols="12" mb-4>
          <helpers-section-sub-header>Date range:</helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="3">
              <v-select
                v-model="clusterCriteria.date_range"
                :items="dateRangeItems"
                item-title="text"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Types of Owners -->
        <v-col cols="12" mb-4>
          <helpers-section-sub-header class="mb-2"> Types of owners: </helpers-section-sub-header>
          <v-select
            v-model="clusterCriteria.owner_types"
            :items="ownerTypes"
            item-title="text"
            density="compact"
            multiple
            chips
            closable-chips
            hide-details
            class="pt-0 d-inline-flex"
          />
        </v-col>

        <!-- Minimum # insiders -->
        <v-col cols="12" xl="2" mb-4>
          <helpers-section-sub-header>Minimum # insiders:</helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="12">
              <v-text-field
                v-model="clusterCriteria.minimum_unique_owners"
                mask="####"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Min. Cluster Amount -->
        <v-col cols="12" xl="2" mb-4>
          <helpers-section-sub-header>Min. cluster amount:</helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="12">
              <v-text-field
                v-model="clusterCriteria.minimum_transaction_amount"
                type="number"
                prefix="$"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Minimum # transactions -->
        <v-col cols="12" xl="2" mb-4>
          <helpers-section-sub-header>Minimum # transactions:</helpers-section-sub-header>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="12">
              <v-text-field
                v-model="clusterCriteria.minimum_transaction_count"
                mask="####"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Optionally filter by Sector/Industry -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header class="mb-2">
            Optionally filter by sector/industry:
          </helpers-section-sub-header>
          <v-treeview
            v-model="clusterCriteria.industries"
            :items="sectors"
            open-on-click
            transition
            select-strategy="classic"
            hoverable
            item-title="name"
            return-object
            selectable
            density="compact"
          />
        </v-col>

        <!-- Filter by stock market cap -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header class="mb-2">
            Filter by stock market cap:
          </helpers-section-sub-header>
          <v-select
            v-model="clusterCriteria.market_caps"
            :items="marketCaps"
            return-object
            item-title="name"
            multiple
            density="compact"
            chips
            closable-chips
            clearable
            hide-details
            class="pt-0 d-inline-flex"
          />
        </v-col>

        <!-- Avg. Stock Price Restrictions -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header class="mb-2">
            Avg. stock price restrictions:
          </helpers-section-sub-header>
          <v-row>
            <v-col :shrink="!$vuetify.display.xs" cols="12">
              <v-text-field
                v-model="clusterCriteria.stock_price_min"
                type="number"
                label="Min price (90-day avg)"
                hide-details
                class="pt-0"
              />
            </v-col>
            <v-col :shrink="!$vuetify.display.xs" cols="12">
              <v-text-field
                v-model="clusterCriteria.stock_price_max"
                type="number"
                label="Max price (90-day avg)"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Avg. Daily Volume -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header class="mb-2"> Avg. daily volume: </helpers-section-sub-header>
          <v-row>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="clusterCriteria.avg_daily_volume_from"
                type="number"
                label="From"
                hide-details
                class="pt-0"
              />
            </v-col>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="clusterCriteria.avg_daily_volume_to"
                type="number"
                label="To"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Avg. Dollar Volume Liquidity -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header class="mb-2">
            Avg. dollar volume liquidity:
          </helpers-section-sub-header>
          <v-row>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="clusterCriteria.avg_dollar_value_volume_min"
                type="number"
                label="From"
                hint="Avg $ value of volume minimum"
                class="pt-0"
              />
            </v-col>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="clusterCriteria.avg_dollar_value_volume_max"
                type="number"
                label="To"
                hint="Avg $ value of volume maximum"
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Use 10b5-1 Transactions -->
        <v-col cols="12">
          <v-checkbox
            v-model="clusterCriteria.is_10b5"
            label="Use 10b5-1 transactions"
            hide-details
            class="mt-0"
          />
        </v-col>

        <!-- Restrict to Buy Reversals Only -->
        <v-col cols="12">
          <v-checkbox
            v-model="clusterCriteria.buy_reversal"
            label="Restrict to Buy reversals only"
            hint="Checking the buy reversal option will limit transactions to only those where an insider purchases new shares after having only sold shares during the past 18 months."
            persistent-hint
            class="mt-0"
          />
        </v-col>
        <v-col cols="12" mb-2>
          <helpers-section-sub-header class="mb-2">
            Purchase stocks only if they are within the below % range of the 52 week high:
          </helpers-section-sub-header>
          <v-row>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="clusterCriteria.restrict_percent_52_week_high"
                type="number"
                label="from"
                class="pt-0"
              />
            </v-col>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="clusterCriteria.restrict_percent_52_week_high_to"
                type="number"
                label="to"
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" mb-2>
          <helpers-section-sub-header class="mb-2">
            Purchase stocks only if they within the below % range of the 52 week low:
          </helpers-section-sub-header>
          <v-row>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="clusterCriteria.restrict_percent_52_week_low"
                type="number"
                label="from"
                class="pt-0"
              />
            </v-col>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="clusterCriteria.restrict_percent_52_week_low_to"
                type="number"
                label="to"
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Purchases Made Near 52-week high -->
        <v-col cols="12">
          <v-checkbox
            v-model="clusterCriteria.restrict_recent_52_week_high"
            label="Purchases made near 52-week high"
            hide-details
            class="mt-0"
          />
        </v-col>

        <!-- Purchases Made Near 52-week low -->
        <v-col cols="12" mb-2>
          <v-checkbox
            v-model="clusterCriteria.restrict_recent_52_week_low"
            label="Purchases made near 52-week low"
            hide-details
            class="mt-0"
          />
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Modules
import { mapStoreState } from '@/store/helpers'
import { mapFields } from '@/store/helpers'

export default {
  data() {
    return {
      dateRangeItems: [
        { value: 0, text: 'Past Day' },
        { value: 1, text: 'Past 3 Days' },
        { value: 2, text: 'Past 7 Days' },
        { value: 6, text: 'Past 2 Weeks' },
        { value: 3, text: 'Past Month' } /*,
        { value: 4, text: 'Past 3 Months' } */
      ],
      ownerTypes: [
        { value: 1, text: '>10% Owner' },
        { value: 2, text: 'Director' },
        { value: 3, text: 'Officer' },
        { value: 5, text: 'C-Level Officer (CEO, COO, CFO, etc.)' },
        { value: 4, text: 'Other Owner' }
      ],
      marketCaps: [
        { name: 'Over $200 billion', id: 1 },
        { name: '$100 billion - $200 billion', id: 2 },
        { name: '$50 billion - $100 billion', id: 3 },
        { name: '$20 billion - $50 billion', id: 4 },
        { name: '$10 billion - $20 billion', id: 5 },
        { name: '$2 billion - $10 billion', id: 6 },
        { name: '$250 million - $2 billion', id: 7 },
        { name: 'Below $250 million', id: 8 }
      ]
    }
  },

  computed: {
    ...mapStoreState('ww', ['sectors']),
    ...mapFields({
      clusterCriteria: 'app.insiderBacktesterClusterCriteria'
    })
  },

  mounted() {
    useWwStore().loadSectors()
  }
}
</script>
